import { FeedType } from '@ms/yammer-data/dist/state/types';
import { Reducer } from 'redux';

import {
  AttachableLinkUrlChangedAction,
  DefaultGroupIdChangedAction,
  FeedIdChangedAction,
  FeedTypeChangedAction,
  IncludeFeedInformationChangedAction,
  IsDarkModeChangedAction,
  PublisherPromptTextChangedAction,
  ShowFooterChangedAction,
  ShowHeaderChangedAction,
  ShowNetworkNameChangedAction,
  ShowWebLinkPreviewChangedAction,
} from '../domains/widgetConfiguration/actions.types';

import { WidgetConfigurationState } from './types';

export const initialState: WidgetConfigurationState = {
  attachableLinkUrl: '',
  defaultGroupId: undefined,
  feedId: undefined,
  feedType: FeedType.Home,
  includeFeedInformation: true,
  isDarkMode: false,
  publisherPromptText: '',
  showFooter: true,
  showHeader: true,
  showNetworkName: true,
  showWebLinkPreview: true,
};

const stateWithUpdatedDefaultGroupId = (
  state: WidgetConfigurationState,
  payload: DefaultGroupIdChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  defaultGroupId: payload.feedId,
});

const stateWithUpdatedFeedType = (
  state: WidgetConfigurationState,
  payload: FeedTypeChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  defaultGroupId: undefined,
  feedType: payload.feedType,
  feedId: undefined,
  attachableLinkUrl: '',
});

const stateWithUpdatedFeedId = (
  state: WidgetConfigurationState,
  payload: FeedIdChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  feedId: payload.feedId,
});

const stateWithUpdatedPublisherPromptText = (
  state: WidgetConfigurationState,
  payload: PublisherPromptTextChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  publisherPromptText: payload.promptText,
});

const stateWithUpdatedAttachableLinkUrl = (
  state: WidgetConfigurationState,
  payload: AttachableLinkUrlChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  attachableLinkUrl: payload.attachableLinkUrl,
});

const stateWithUpdatedShowHeader = (
  state: WidgetConfigurationState,
  payload: ShowHeaderChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  showHeader: payload.showHeader,
});

const stateWithUpdatedShowFooter = (
  state: WidgetConfigurationState,
  payload: ShowFooterChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  showFooter: payload.showFooter,
});

const stateWithUpdatedShowNetworkName = (
  state: WidgetConfigurationState,
  payload: ShowNetworkNameChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  showNetworkName: payload.showNetworkName,
});

const stateWithUpdatedShowWebLinkPreview = (
  state: WidgetConfigurationState,
  payload: ShowWebLinkPreviewChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  showWebLinkPreview: payload.showWebLinkPreview,
});

const stateWithUpdatedIsDarkMode = (
  state: WidgetConfigurationState,
  payload: IsDarkModeChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  isDarkMode: payload.isDarkMode,
});

const stateWithUpdatedIncludeFeedInformation = (
  state: WidgetConfigurationState,
  payload: IncludeFeedInformationChangedAction['payload']
): WidgetConfigurationState => ({
  ...state,
  includeFeedInformation: payload.includeFeedInformation,
});

const reducer: Reducer<WidgetConfigurationState> = (state = initialState, action) => {
  switch (action.type) {
    case 'EMBED.DEFAULT_GROUP_ID_CHANGED':
      return stateWithUpdatedDefaultGroupId(state, action.payload);
    case 'EMBED.FEED_TYPE_CHANGED':
      return stateWithUpdatedFeedType(state, action.payload);
    case 'EMBED.FEED_ID_CHANGED':
      return stateWithUpdatedFeedId(state, action.payload);
    case 'EMBED.PUBLISHER_PROMPT_TEXT_CHANGED':
      return stateWithUpdatedPublisherPromptText(state, action.payload);
    case 'EMBED.ATTACHABLE_LINK_URL_CHANGED':
      return stateWithUpdatedAttachableLinkUrl(state, action.payload);
    case 'EMBED.SHOW_HEADER_CHANGED':
      return stateWithUpdatedShowHeader(state, action.payload);
    case 'EMBED.SHOW_FOOTER_CHANGED':
      return stateWithUpdatedShowFooter(state, action.payload);
    case 'EMBED.SHOW_NETWORK_NAME_CHANGED':
      return stateWithUpdatedShowNetworkName(state, action.payload);
    case 'EMBED.SHOW_WEB_LINK_PREVIEW_CHANGED':
      return stateWithUpdatedShowWebLinkPreview(state, action.payload);
    case 'EMBED.IS_DARK_MODE_CHANGED':
      return stateWithUpdatedIsDarkMode(state, action.payload);
    case 'EMBED.INCLUDE_FEED_INFORMATION_CHANGED':
      return stateWithUpdatedIncludeFeedInformation(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
